import { ReportContext } from '@/contexts/reportContext';
import { DashboardWidget } from '@/store/dashboardApi';
import { ContentContainerVariant } from '@/widgets/contentContainer';
import { Box, CircularProgress } from '@mui/material';
import React, {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// eslint-disable-next-line react-refresh/only-export-components
export const useFilteredDashboardWidgets = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  widgetMap: Record<string, React.LazyExoticComponent<any>>,
  widgets: DashboardWidget[],
  options?: { includeHidden?: boolean },
) => {
  const includeHidden = options?.includeHidden ?? false;

  return useMemo(() => {
    return widgets
      .filter(
        (widget) =>
          Object.keys(widgetMap).includes(widget.Code) &&
          (includeHidden || widget.IsVisible),
      )
      .sort((a, b) => a.Position - b.Position);
  }, [widgets, includeHidden, widgetMap]);
};

interface WidgetLoaderProps {
  code: string;
  widgetMap: Record<
    string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    React.LazyExoticComponent<React.ComponentType<any>>
  >;
  onDataLoaded?: () => void;
  excludedAccounts?: number[];
  excludedAccountTypes?: number[];
  excludedCategories?: number[];
  isHalfWidth?: boolean;
  settings?: Array<{ key: string; value: string }>;
  title?: string;
  container: ContentContainerVariant;
  [key: string]: unknown;
}

export const WidgetLoader: React.FC<WidgetLoaderProps> = ({
  code,
  widgetMap,
  onDataLoaded,
  excludedAccounts,
  excludedAccountTypes,
  excludedCategories,
  isHalfWidth,
  settings,
  container,
  ...rest
}) => {
  const { registerWidget, widgetLoaded } = useContext(ReportContext);

  const WidgetComponent = widgetMap[
    code
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ] as unknown as React.ComponentType<any>;

  useEffect(() => {
    registerWidget();
  }, [registerWidget]);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!WidgetComponent) {
    return null;
  }

  const handleLoaded = () => {
    widgetLoaded();
    onDataLoaded?.();
  };

  return (
    <Suspense
      fallback={
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          height='100%'
        >
          <CircularProgress />
        </Box>
      }
    >
      <WidgetComponent
        {...rest}
        isWidget={true}
        isHalfWidth={isHalfWidth}
        settings={settings}
        onDataLoaded={handleLoaded}
        excludedAccounts={excludedAccounts}
        excludedAccountTypes={excludedAccountTypes}
        excludedCategories={excludedCategories}
        container={container}
      />
    </Suspense>
  );
};

interface WidgetArrayLoaderProps {
  widgets: DashboardWidget[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  widgetMap: Record<string, React.LazyExoticComponent<any>>;
  onWidgetLoaded?: () => void;
  excludedAccounts?: number[];
  excludedAccountTypes?: number[];
  excludedCategories?: number[];
  widgetPropsByCode?: Record<string, Record<string, unknown>>;
  /**
   * Layout mode for dashboard widgets.
   * - `grid`: stable row/column grid (default)
   * - `masonry`: grid-based masonry using row spans (preserves DOM order)
   */
  layout?: 'grid' | 'masonry';
}

const MASONRY_ROW_HEIGHT_PX = 8;

function useMasonryRowSpans(options: {
  enabled: boolean;
  rowHeightPx: number;
  /**
   * Row gap used by the grid, in px. Used to compute a slightly more accurate span.
   * If you change the container `gap`, update this value too.
   */
  gapPx: number;
}): {
  /**
   * Returns a stable ref callback per id (cached) to avoid React detaching/attaching
   * refs on every render, which can cause render loops when the ref callback sets state.
   */
  getItemRef: (id: number) => (el: HTMLDivElement | null) => void;
  getRowSpan: (id: number) => number | undefined;
} {
  const { enabled, rowHeightPx, gapPx } = options;
  const elementsByIdRef = useRef(new Map<number, HTMLDivElement>());
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const refCallbackByIdRef = useRef(
    new Map<number, (el: HTMLDivElement | null) => void>(),
  );
  const [rowSpanById, setRowSpanById] = useState<Map<number, number>>(
    () => new Map(),
  );

  const computeSpanForEl = useCallback(
    (height: number): number => {
      if (!Number.isFinite(height) || height <= 0) {
        return 1;
      }
      // grid-auto-rows sets the baseline row height; gap is between rows.
      // Approximate the number of rows consumed by including the gap in the denominator.
      const denom = rowHeightPx + gapPx;
      const numer = height + gapPx;
      return Math.max(1, Math.ceil(numer / denom));
    },
    [gapPx, rowHeightPx],
  );

  useEffect(() => {
    if (!enabled) {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      return;
    }
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const ro = new ResizeObserver((entries) => {
      setRowSpanById((prev) => {
        let didChange = false;
        const next = new Map(prev);

        for (const entry of entries) {
          const el = entry.target as HTMLDivElement;
          const idAttr = el.dataset['masonryId'];
          const id = idAttr ? Number.parseInt(idAttr, 10) : NaN;
          if (!Number.isFinite(id)) continue;

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          const height = entry.contentRect?.height ?? 0;
          const span = computeSpanForEl(height);
          if (next.get(id) !== span) {
            next.set(id, span);
            didChange = true;
          }
        }

        return didChange ? next : prev;
      });
    });
    resizeObserverRef.current = ro;

    // Observe all currently registered elements (covers initial mount)
    for (const el of elementsByIdRef.current.values()) {
      ro.observe(el);
    }

    return () => {
      ro.disconnect();
      if (resizeObserverRef.current === ro) {
        resizeObserverRef.current = null;
      }
    };
  }, [computeSpanForEl, enabled]);

  const setItemRefImpl = useCallback(
    (id: number, el: HTMLDivElement | null) => {
      const map = elementsByIdRef.current;
      if (!enabled) {
        return;
      }
      if (!el) {
        const existing = map.get(id);
        if (existing) {
          resizeObserverRef.current?.unobserve(existing);
        }
        map.delete(id);
        setRowSpanById((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Map(prev);
          next.delete(id);
          return next;
        });
        return;
      }
      el.dataset['masonryId'] = String(id);
      map.set(id, el);
      resizeObserverRef.current?.observe(el);

      // Set an initial span immediately so tiles don't render as 1-row tall before
      // the first ResizeObserver callback fires (which causes the "scrunched" look).
      const setSpanFromEl = () => {
        const height = el.getBoundingClientRect().height;
        const span = computeSpanForEl(height);
        setRowSpanById((prev) => {
          const prevSpan = prev.get(id);
          if (prevSpan === span) {
            return prev;
          }
          const next = new Map(prev);
          next.set(id, span);
          return next;
        });
      };
      setSpanFromEl();
      // Re-measure next frame after layout settles / async content mounts.
      requestAnimationFrame(setSpanFromEl);
    },
    [computeSpanForEl, enabled],
  );

  const getItemRef = useCallback(
    (id: number) => {
      const existing = refCallbackByIdRef.current.get(id);
      if (existing) return existing;

      const cb = (el: HTMLDivElement | null) => {
        setItemRefImpl(id, el);
      };
      refCallbackByIdRef.current.set(id, cb);
      return cb;
    },
    [setItemRefImpl],
  );

  const getRowSpan = useCallback(
    (id: number) => rowSpanById.get(id),
    [rowSpanById],
  );

  return { getItemRef, getRowSpan };
}

export const WidgetArrayLoader: React.FC<WidgetArrayLoaderProps> = ({
  widgets,
  widgetMap,
  onWidgetLoaded,
  excludedAccounts,
  excludedAccountTypes,
  excludedCategories,
  widgetPropsByCode,
  layout = 'grid',
}) => {
  const isMasonry = layout === 'masonry';
  // Keep this in sync with the container `gap: 3` (theme.spacing(3) = 24px).
  const masonry = useMasonryRowSpans({
    enabled: isMasonry,
    rowHeightPx: MASONRY_ROW_HEIGHT_PX,
    gapPx: 24,
  });

  const gridItems = useMemo(() => {
    return widgets.map((widget) => {
      const extraProps = widgetPropsByCode?.[widget.Code] ?? {};
      const rowSpan = isMasonry
        ? masonry.getRowSpan(widget.WidgetId)
        : undefined;

      return (
        <Box
          key={widget.WidgetId}
          sx={{
            // Full-width widgets span the entire grid row. Half-width widgets flow normally.
            gridColumn: widget.IsHalfWidth ? 'auto' : '1 / -1',
            minWidth: 0,
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            ...(isMasonry && rowSpan ? { gridRowEnd: `span ${rowSpan}` } : {}),
          }}
        >
          <Box
            ref={isMasonry ? masonry.getItemRef(widget.WidgetId) : undefined}
          >
            <WidgetLoader
              container='widget'
              code={widget.Code}
              widgetMap={widgetMap}
              onDataLoaded={onWidgetLoaded}
              excludedAccounts={excludedAccounts}
              excludedAccountTypes={excludedAccountTypes}
              excludedCategories={excludedCategories}
              isHalfWidth={widget.IsHalfWidth}
              settings={widget.Settings}
              title={widget.Title}
              {...extraProps}
            />
          </Box>
        </Box>
      );
    });
  }, [
    widgets,
    widgetMap,
    onWidgetLoaded,
    excludedAccounts,
    excludedAccountTypes,
    excludedCategories,
    widgetPropsByCode,
    // Critical: in masonry mode, row spans update over time as widgets load/resize.
    // We must re-render the grid items when the span map changes (getRowSpan identity changes).
    isMasonry,
    masonry,
  ]);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        ...(isMasonry
          ? {
              // Grid-based masonry (no gaps) using row spans computed via ResizeObserver.
              // Preserves DOM order (Customize ordering) while allowing tiles to "float up"
              // within their column.
              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
              gridAutoRows: `${MASONRY_ROW_HEIGHT_PX}px`,
              alignItems: 'start',
              // Cap at two columns so a half-width widget is always half the
              // row, then fall back to one column when that would be too narrow.
              gridTemplateColumns:
                'repeat(auto-fit, minmax(min(100%, max(320px, (100% - 24px) / 2)), 1fr))',
            }
          : {
              // Stable grid layout.
              gridTemplateColumns:
                'repeat(auto-fit, minmax(min(380px, 100%), 1fr))',
            }),
        gap: 3,
      }}
    >
      {gridItems}
    </Box>
  );
};

export default WidgetLoader;
